import { useLocation, useNavigate } from 'react-router-dom';
import { titleForPath, isCityLanding, ROUTES } from '@/config/navigation';

/**
 * Barre de repère, sur téléphone uniquement.
 *
 * Deux manques qu'elle comble, tous deux invisibles sur grand écran :
 *
 * · On ne savait plus où on était. La colonne latérale qui porte le nom de la
 *   section est masquée sous 1024 px, et l'en-tête ne montre que le logo.
 *
 * · On ne pouvait revenir en arrière qu'avec le bouton du navigateur — que
 *   beaucoup n'utilisent pas, et qui disparaît en mode application installée.
 *
 * Elle ne s'affiche pas sur l'accueil : il n'y a ni titre à rappeler, ni
 * arrière où revenir.
 */
export function MobilePageBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const title = isCityLanding(pathname)
    ? 'Location de voiture'
    : titleForPath(pathname);

  if (pathname === ROUTES.home || !title) return null;

  /**
   * Revenir en arrière — sans jamais sortir du site.
   *
   * `navigate(-1)` seul est un piège : quelqu'un qui arrive directement sur une
   * page par un lien partagé n'a pas d'historique, et le bouton le renverrait
   * d'où il vient, c'est-à-dire hors de l'application. On regarde donc si React
   * Router a déjà empilé quelque chose ; sinon, on remonte à l'accueil.
   */
  function goBack() {
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (index > 0) navigate(-1);
    else navigate(ROUTES.home);
  }

  return (
    <div className="sticky top-[72px] z-30 border-b border-ink-200 bg-white/95 backdrop-blur md:hidden">
      <div className="cf-container flex h-12 items-center gap-2">
        <button
          type="button"
          onClick={goBack}
          aria-label="Revenir en arrière"
          className="-ml-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-ink-700 hover:bg-ink-50"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
            <path d="M15 5l-7 7 7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <p className="min-w-0 flex-1 truncate text-[15px] font-bold text-ink-950">{title}</p>
      </div>
    </div>
  );
}
